// MACHINERY V2 (Stroje) — catalog model detail page (M2.8).
//
// Spec: data package §11 (detail route + states). Reference impl:
// components/CatalogModelDetailPage.tsx; artboard 0902 — 380px identity column
// on the left, the spec sheet on the right.
//
// >>> §2.3 DEVIATION, STATED HERE TOO: THE SPEC SHEET IS NOT `MachineSpecs`. <<<
// The M2.8 ticket row says "reuse `MachineSpecs`". That component imports MUI
// and `dt` (MachineSpecs.tsx:2-16 and :20) and cannot enter the v2 import graph
// under §3. The MUI-free v2 spec stack shipped at M2.5 does the same job —
// `resolveSpecSheet` for the classification/description rules, `SpecSheetView`
// for the toolbar + sections, `convertSpec` inside `SpecRow` for the
// metric/imperial toggle — so the page reuses THAT. Full reasoning in
// profile/tabs/SpecSheetView.tsx's header.
//
// >>> `hasFetchedRef` AND `clearSelectedModel` ARE BOTH PRESERVED. <<<
// `selectedModel` is a SHARED slice slot: the profile's Špecifikácie tab reads
// it too. v1 guards the "Model not found" flash with `hasFetchedRef` (the slot
// is empty for one render before the first fetch resolves) and clears the slot
// on unmount so a later visit cannot render a stale model. BOTH are kept — and
// the id guard the v2 tab added is applied here as well, so an arriving
// response for a PREVIOUS model never paints under this one. (The tab
// deliberately does NOT clear on unmount; this page owns that lifecycle. See
// SpecificationsTabV2.tsx:16-26.)
//
// §11 "Error: thunks rejected only set loading=false — NO error UI, renders as
// empty / 'Model not found'." Fixed here the same way the browse page fixes it:
// the failure is tracked off the dispatch promise and gets a real RetryNotice.
// "Model not found" was also a HARDCODED English string (§11 States) and is now
// a key in both locales.
//
// >>> SPLIT AT THE M2.16c FIX PASS. <<< This file crossed the 300-line cap
// taking the review's sticky-CTA clearance fix, so the page-level overlay
// mounts moved to CatalogDetailOverlays.tsx — the seam CatalogOverlays.tsx and
// FleetOverlays.tsx already took from their own pages. Same elements, same
// order, same depth: that file returns a fragment.
//
// ============================================================
//  THE 1280 BRANCH (M2.16c) — AND WHY DESKTOP IS UNTOUCHED
// ============================================================
// Rendered truth below 1280: mobile/screenshots/17-katalog-detail.png. The
// desktop's breadcrumb band + title band are replaced by a single back link, the
// 380px aside + spec-sheet row is replaced by CatalogDetailMobile's single
// column, and the Write-gated CTA moves from inside the aside to a sticky
// bottom bar with a gradient scrim.
//
// >>> NO DECLARATION THE DESKTOP ELEMENT NEVER HAD IS EMITTED. <<< The sticky
// CTA needs a positioning context, so the page container takes
// `position: relative` — BELOW 1280 ONLY, spread conditionally. At >= 1280 the
// style object is what it was, with no `position` key at all, so nothing about
// how an existing absolutely-positioned descendant resolves can change.
//
// The `isPhone` this file already read (for SpecSheetView's dense mode) is now
// read alongside `isDesktop`; both come from `useV2MediaQuery` (finding F-A) —
// the module's own queries, never MUI's and never an integer width.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import usePermission from "../../../../shared/hooks/usePermission";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, V2_PHONE, useV2MediaQuery } from "../useV2MediaQuery";
import { Modules } from "../../../../core/models/PermissionModels";
import { CatalogModelListItem } from "../../models/models";
import { clearSelectedModel, getCatalogModelDetail } from "../../machineCatalogSlice";
import RetryNotice from "../feedback/RetryNotice";
import {
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "../readiness/readinessPresentation";
import { resolveSpecSheet } from "../profile/tabs/specResolve";
import SpecSheetView from "../profile/tabs/SpecSheetView";
import { TabCard, TabEmpty } from "../profile/tabs/tabAtoms";
import { SkeletonList } from "../profile/tabs/tabSkeletons";
import CatalogDetailAside from "./CatalogDetailAside";
import CatalogDetailMobile from "./CatalogDetailMobile";
import CatalogDetailOverlays from "./CatalogDetailOverlays";
import {
  CatalogDetailBackLink,
  CatalogDetailStickyCta,
} from "./catalogDetailMobileParts";
import { CatalogBreadcrumb, CatalogTitle, catalogAddedPath } from "./catalogChrome";
import { detailAsListItem } from "./catalogDetailAdapter";

/** The scroll area's landing strip for the sticky CTA. The CTA is
 *  `10 + 48 + max(14px, env(safe-area-inset-bottom))` tall
 *  (catalogDetailMobileParts.tsx `CatalogDetailStickyCta`), i.e. 92px on a phone
 *  reporting a 34px inset — so the flat 84px this used to be left the last card
 *  under the scrim on exactly the devices `env()` exists for (M2.16c review,
 *  item 5). 70 = 12px clearance + the CTA's own 10 + 48. */
const MOBILE_SCROLL_PAD = "max(84px, calc(70px + env(safe-area-inset-bottom)))";

const CatalogDetailV2: React.FC = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate, getCurrentLanguage } = useTranslation(["myMachinery", "common"]);
  // F-A: the module's own queries — never an integer width, never MUI.
  const isPhone = useV2MediaQuery(V2_PHONE);
  const isDesktop = !useV2MediaQuery(V2_BELOW_DESKTOP);
  const { canWrite } = usePermission(Modules.Machinery);

  const selectedModel = useAppSelector((s) => s.machineCatalog.selectedModel);
  const loading = useAppSelector((s) => s.machineCatalog.selectedModelLoading);

  const [failed, setFailed] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const hasFetchedRef = useRef(false);

  const load = useCallback(() => {
    if (!modelId) return;
    setFailed(false);
    hasFetchedRef.current = true;
    dispatch(getCatalogModelDetail(modelId))
      .unwrap()
      .catch(() => setFailed(true));
  }, [dispatch, modelId]);

  useEffect(() => {
    load();
    return () => {
      dispatch(clearSelectedModel());
    };
  }, [load, dispatch]);

  /** The shared-slot id guard — never paint another model's data here. */
  const model = selectedModel?.id === modelId ? selectedModel : undefined;
  const currentLang = getCurrentLanguage();

  const summaryStrings = useMemo(() => readinessSummaryStrings(translate), [translate]);
  const presentation = useMemo(
    () => (model ? deriveReadinessPresentation(model, undefined, summaryStrings) : null),
    [model, summaryStrings]
  );

  const sheet = useMemo(
    () => resolveSpecSheet(model?.specSections, currentLang),
    [model, currentLang]
  );

  /** The add dialog and the readiness popup both speak the LIST-item shape;
   *  the detail VM is a superset minus a few flattened names (§11's closing
   *  paragraph). One adapter, so neither has to know which page opened it. */
  const asListItem: CatalogModelListItem | null = useMemo(
    () => (model ? detailAsListItem(model) : null),
    [model]
  );

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        flexDirection: "column",
        // Positioning context for the sticky CTA — below 1280 ONLY. Spread
        // conditionally so the desktop element emits no `position` declaration
        // it did not have before this sub-phase.
        ...(isDesktop ? null : { position: "relative" as const }),
      }}
    >
      {isDesktop ? (
        <>
          <CatalogBreadcrumb current={model ? model.modelName : undefined} />
          <CatalogTitle />
        </>
      ) : (
        <CatalogDetailBackLink />
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // The extra bottom padding is the sticky CTA's landing strip: without
          // it the last card sits UNDER the scrim and cannot be scrolled clear.
          padding: isDesktop ? "14px 24px 24px" : "12px 14px 84px",
          // Progressive enhancement in this order, the same shape the CTA itself
          // uses: an unsupported `env()` makes this declaration invalid, the
          // browser drops it, and the flat 84px above still stands.
          ...(isDesktop ? null : { paddingBottom: MOBILE_SCROLL_PAD }),
        }}
      >
        {failed && (
          <div style={{ marginBottom: 12 }}>
            <RetryNotice
              title={translate("myMachinery:v2.catalog.loadFailed")}
              hint={translate("myMachinery:v2.tabs.loadFailedHint")}
              onRetry={load}
              busy={loading}
            />
          </div>
        )}

        {!model && (loading || !hasFetchedRef.current) && !failed && (
          <TabCard>
            <SkeletonList rows={5} height={40} />
          </TabCard>
        )}

        {/* Only ONCE a fetch has actually happened and did not fail — the
            `hasFetchedRef` guard is what stops the flash v1 documented. */}
        {!model && !loading && !failed && hasFetchedRef.current && (
          <TabEmpty title={translate("myMachinery:v2.catalog.modelNotFound")} />
        )}

        {model &&
          presentation &&
          asListItem &&
          (isDesktop ? (
            <div
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-start",
                minWidth: 0,
                // Kept for the desktop's own narrow end (1280-1400), where the
                // 380px aside plus a 320px minimum still needs somewhere to go.
                flexWrap: "wrap",
              }}
            >
              <CatalogDetailAside
                model={model}
                presentation={presentation}
                canWrite={canWrite}
                onAdd={() => setAddOpen(true)}
                onOpenReadiness={() => setReadinessOpen(true)}
              />

              <div
                style={{
                  flex: 1,
                  minWidth: 320,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <SpecSheetView
                  key={model.id}
                  sheet={sheet}
                  label={translate("myMachinery:specifications")}
                  phone={isPhone}
                />
              </div>
            </div>
          ) : (
            <CatalogDetailMobile
              model={model}
              presentation={presentation}
              sheet={sheet}
              phone={isPhone}
              onOpenReadiness={() => setReadinessOpen(true)}
            />
          ))}
      </div>

      {/* Board 17's bottom-pinned primary. A SIBLING of the scroll area, not a
          child of it, so it stays put while the content moves. Write-gated
          exactly as the aside's CTA is (§11) — a Worker sees neither. */}
      {!isDesktop && model && canWrite && (
        <CatalogDetailStickyCta
          label={translate("myMachinery:v2.catalog.addFromCatalogCta")}
          onClick={() => setAddOpen(true)}
        />
      )}

      {/* Page-level siblings, outside every clickable ancestor — same rule as
          the browse page and FleetPage.tsx:231-245, and the reason
          CatalogDetailOverlays returns a fragment rather than a container. */}
      <CatalogDetailOverlays
        model={model}
        presentation={presentation}
        asListItem={asListItem}
        readinessOpen={readinessOpen}
        onCloseReadiness={() => setReadinessOpen(false)}
        addOpen={addOpen}
        onCloseAdd={() => setAddOpen(false)}
        onCreated={(id) => navigate(catalogAddedPath(id))}
      />
    </div>
  );
};

export default CatalogDetailV2;
